import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  useSellerAdsAgent,
  useSellerAdsBoard,
  type AdsBoardCard,
  type AdsCampaignCard,
  type AdsOpportunity,
  type AdsPendingAction,
  type AdsAnalysisDetail,
} from '../../hooks/useSellerAdsAgent';
import { PlatformIcon } from '../shared/PlatformIcon';
import { CreditConfirmRunButton } from '../credits/CreditConfirmRunButton';
import { api } from '../../services/api';
import { AlertCard, ACTION_TYPE_LABELS, CLASSIFICATION_STYLES, describeParams, fmtMoney, PendingActionRow, WindowRow } from './adsAgentShared';
import { SellerAutomationHeader, SellerRunButton, sellerRunButtonClass, sellerHeaderSelectClass } from './SellerAutomationHeader';
import { BoardGroupingToggle, Drawer, EmptyState, FlowBoard, HealthBoard, KpiTile, ReviewTag, SegmentedToggle, URGENCY_BADGE, flowStageOf, marketplaceName, useBoardGrouping, type HealthColumn, type OverflowItem } from './sellerUi';

// ---------------------------------------------------------------------------
// Tela "Ads": antes eram duas ("Metricas de Ads" com os numeros e "Saude de Ads"
// com o kanban da IA). Agora e uma so, no mesmo formato da tela Anuncios:
//   Tabela -> numeros da campanha + etiqueta de saude
//   Quadro -> etapas de trabalho (para revisar, revisando, aguardando, tudo certo)
//   Painel -> janelas 7/14/30d, diagnostico, acoes prontas para executar
// ---------------------------------------------------------------------------

type AdsMode = 'tabela' | 'quadro';

// "aprendizado" ainda nao da para julgar: fica em "Aguardando efeito".
// "oportunidade" (escalar) pede acao, entao cai em "Para revisar".
const ADS_HEALTH_COLUMNS: HealthColumn[] = [
  { id: 'critico', label: 'Crítico', dot: 'bg-red-500', head: 'text-red-700 border-red-200 bg-red-50' },
  { id: 'atencao', label: 'Atenção', dot: 'bg-amber-400', head: 'text-amber-700 border-amber-200 bg-amber-50' },
  { id: 'oportunidade', label: 'Oportunidade (escalar)', dot: 'bg-emerald-500', head: 'text-emerald-700 border-emerald-200 bg-emerald-50' },
  { id: 'aprendizado', label: 'Aprendizado', dot: 'bg-blue-500', head: 'text-blue-700 border-blue-200 bg-blue-50' },
  { id: 'saudavel', label: 'Saudável', dot: 'bg-green-500', head: 'text-green-700 border-green-200 bg-green-50' },
];

const OK_COLUMNS = ['saudavel'];
const WAITING_COLUMNS = ['aprendizado'];

function adsFlowStage(card: AdsBoardCard) {
  return flowStageOf(card, OK_COLUMNS, WAITING_COLUMNS);
}

const REVIEW_LABEL: Record<string, string> = { pendente: 'Pendente', revisado: 'Revisando', corrigido: 'Corrigido', ignorado: 'Ignorado' };

function campaignKey(c: { connectionId: string; campaignIdExt: string }) {
  return `${c.connectionId}:${c.campaignIdExt}`;
}

function AdsBoardCardView({ card, onClick }: { card: AdsBoardCard; onClick: () => void }) {
  const urg = URGENCY_BADGE[card.urgency] || URGENCY_BADGE.nenhuma;
  const cls = CLASSIFICATION_STYLES[card.classification];
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClick(); }}
      className="w-full cursor-pointer rounded-lg border border-gray-200 bg-white p-3 text-left shadow-sm transition hover:border-orange-300 hover:shadow"
    >
      <div className="flex items-start gap-2">
        <span className="mt-0.5 inline-flex items-center rounded bg-gray-50 p-0.5" title={marketplaceName(card.marketplace)}><PlatformIcon platform={card.marketplace} size={14} /></span>
        <p className="line-clamp-2 min-w-0 flex-1 text-xs font-medium text-gray-800" title={card.campaignName}>{card.campaignName}</p>
      </div>
      <div className="mt-1.5 flex flex-wrap items-center gap-1">
        <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${urg.cls}`}>{urg.label}</span>
        {cls && <span className={`rounded px-1.5 py-0.5 text-[10px] ${cls.badge}`}>{card.classificationLabel || cls.label}</span>}
        {card.analysisNumber > 1 && <span className="rounded bg-purple-100 px-1.5 py-0.5 text-[10px] text-purple-700">{card.analysisNumber}ª análise</span>}
        <ReviewTag reviewStatus={card.reviewStatus} cooldownUntil={card.cooldownUntil} />
      </div>
      {card.headline && <p className="mt-1.5 line-clamp-2 text-[11px] text-gray-600">{card.headline}</p>}
      <div className="mt-2 flex flex-wrap gap-x-2 text-[10px] text-gray-500">
        <span>ROAS {card.metrics.roas}x</span>
        <span>ACOS {card.metrics.acos >= 999 ? '—' : `${card.metrics.acos}%`}</span>
        <span>Gasto {fmtMoney(card.metrics.spend)}</span>
      </div>
      {card.pendingActionsCount > 0 && (
        <div className="mt-2 rounded bg-emerald-50 px-2 py-1 text-[10px] font-semibold text-emerald-700">{card.pendingActionsCount} ação(ões) pronta(s) pra executar</div>
      )}
    </div>
  );
}

function OpportunityRow({ op }: { op: AdsOpportunity }) {
  const categoryLabels: Record<string, string> = {
    hot_product_no_ads: 'Produto quente sem Ads',
    good_conversion_low_traffic: 'Converte bem, pouco tráfego',
    organic_signal: 'Sinal orgânico',
  };
  return (
    <div className="flex items-center gap-3 rounded-lg border border-emerald-200 bg-emerald-50/50 px-3 py-2">
      {op.imageUrl ? <img src={op.imageUrl} alt="" className="h-9 w-9 shrink-0 rounded object-cover" /> : <div className="h-9 w-9 shrink-0 rounded bg-gray-100" />}
      <div className="min-w-0 flex-1">
        <p className="truncate text-xs font-semibold text-gray-800">{op.title || op.listingIdExt}</p>
        <p className="text-[10px] text-gray-500">{categoryLabels[op.category] || op.category} · {op.orders} pedidos orgânicos 30d · conv. {op.conversion}% · {fmtMoney(op.revenue)}</p>
      </div>
      <div className="shrink-0 text-center">
        <div className="text-sm font-bold text-emerald-700">{op.score}</div>
        <div className="text-[9px] uppercase text-emerald-500">score</div>
      </div>
    </div>
  );
}

function List({ title, items, tone = 'text-gray-600' }: { title: string; items: string[]; tone?: string }) {
  if (items.length === 0) return null;
  return (
    <div className="mt-2">
      <p className="text-[10px] font-semibold uppercase text-gray-400">{title}</p>
      <ul className="mt-0.5 space-y-0.5">{items.map((e, i) => <li key={i} className={`text-[11px] ${tone}`}>• {e}</li>)}</ul>
    </div>
  );
}

function AdsCampaignDrawer({
  name, marketplace, campaign, boardCard, onClose, fetchDetail, onExecute, onDismiss, onReview, onToggleAnalysis, executingId,
}: {
  name: string;
  marketplace: string;
  campaign?: AdsCampaignCard;
  boardCard?: AdsBoardCard;
  onClose: () => void;
  fetchDetail: (id: string) => Promise<AdsAnalysisDetail | null>;
  onExecute: (a: AdsPendingAction) => void;
  onDismiss: (a: AdsPendingAction) => void;
  onReview: (id: string, status: string) => void;
  onToggleAnalysis: (campaignIdExt: string, enabled: boolean) => void;
  executingId: string | null;
}) {
  const [detail, setDetail] = useState<AdsAnalysisDetail | null>(null);
  const [loading, setLoading] = useState(Boolean(boardCard));
  const analysisEnabled = boardCard?.analysisEnabled ?? campaign?.analysisEnabled ?? true;
  const campaignIdExt = boardCard?.campaignIdExt || campaign?.campaignIdExt || '';

  useEffect(() => {
    if (!boardCard) return;
    let stop = false;
    setLoading(true);
    fetchDetail(boardCard.id).then((d) => { if (!stop) { setDetail(d); setLoading(false); } });
    return () => { stop = true; };
  }, [boardCard?.id, fetchDetail]);

  const diag = (detail?.analysis?.diagnosisJson || {}) as Record<string, unknown>;
  const arr = (k: string): string[] => (Array.isArray(diag[k]) ? (diag[k] as string[]) : []);
  const plan = Array.isArray(diag.planoDeAcao) ? (diag.planoDeAcao as Array<{ tipo: string; acao: string; justificativa: string; impactoEsperado: string }>) : [];
  const pending = detail?.pendingActions || boardCard?.pendingActions || campaign?.pendingActions || [];
  const reviewStatus = boardCard?.reviewStatus || 'pendente';

  return (
    <Drawer
      open
      onClose={onClose}
      title={name}
      subtitle={[marketplaceName(marketplace), boardCard?.classificationLabel || campaign?.classificationLabel, boardCard?.lifecycleStage || campaign?.lifecycleStage, campaign?.status].filter(Boolean).join(' · ')}
      footer={boardCard ? (
        <div className="flex flex-wrap items-center gap-2">
          {reviewStatus !== 'revisado' && <button onClick={() => onReview(boardCard.id, 'revisado')} className="rounded-md bg-sky-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-sky-700">Estou revisando</button>}
          {reviewStatus !== 'corrigido' && <button onClick={() => onReview(boardCard.id, 'corrigido')} className="rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700">Corrigi</button>}
          {reviewStatus !== 'ignorado' && <button onClick={() => onReview(boardCard.id, 'ignorado')} className="rounded-md border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50">Ignorar</button>}
          {reviewStatus !== 'pendente' && <button onClick={() => onReview(boardCard.id, 'pendente')} className="rounded-md border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50">Voltar pra revisar</button>}
          <span className="ml-auto text-[10px] text-gray-400">Status: {REVIEW_LABEL[reviewStatus] || reviewStatus}</span>
        </div>
      ) : undefined}
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-500">
          {campaign?.budget != null && <span>Orçamento: {fmtMoney(campaign.budget)}{campaign.budgetType === 'automatic' ? ' (auto)' : '/dia'}</span>}
          {campaign?.roasTarget != null && <span>Meta ROAS: {campaign.roasTarget}x</span>}
          {campaign?.strategy && <span>Estratégia: {campaign.strategy}</span>}
          {campaign && campaign.itemCount > 0 && <span>{campaign.itemCount} item(ns)</span>}
          {campaignIdExt && (
            <label className="ml-auto inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-gray-200 px-2 py-1 font-medium text-gray-600">
              <input type="checkbox" checked={analysisEnabled} onChange={(e) => onToggleAnalysis(campaignIdExt, e.target.checked)} className="h-3.5 w-3.5 rounded border-gray-300 text-orange-600 focus:ring-orange-500" />
              Analisar com IA
            </label>
          )}
        </div>

        {campaign && (
          <section className="space-y-1.5">
            {campaign.metricsGranularity === 'window' ? (
              <>
                <WindowRow title={`Período (${campaign.metricsWindowDays || 30}d)`} w={campaign.d30} />
                <p className="text-[10px] text-gray-400">O Mercado Livre não entregou série diária para esta campanha; mostrando o total do período.</p>
              </>
            ) : (
              <>
                <WindowRow title="7 dias" w={campaign.d7} />
                <WindowRow title="14 dias" w={campaign.d14} />
                <WindowRow title="30 dias" w={campaign.d30} />
              </>
            )}
          </section>
        )}

        {!boardCard ? (
          <div className="rounded-lg border border-dashed border-gray-300 p-4 text-center text-xs text-gray-500">
            Esta campanha ainda não foi analisada pela IA. Use "Analisar com IA" no topo da tela.
          </div>
        ) : loading ? (
          <p className="py-6 text-center text-sm text-gray-400">Carregando análise...</p>
        ) : (
          <>
            {pending.length > 0 && (
              <section>
                <p className="mb-1.5 text-[10px] font-bold uppercase tracking-wide text-emerald-700">Pronto pra executar</p>
                <div className="space-y-1.5">
                  {pending.map((a) => <PendingActionRow key={a.id} action={a} onExecute={onExecute} onDismiss={onDismiss} busy={executingId === a.id} />)}
                </div>
              </section>
            )}

            <section className="rounded-lg border border-orange-100 bg-orange-50/50 p-3">
              <span className="text-[10px] font-bold uppercase tracking-wide text-orange-700">Diagnóstico da IA</span>
              {typeof diag.headline === 'string' && <p className="mt-1 text-sm font-semibold text-gray-800">{diag.headline}</p>}
              {typeof diag.resumo === 'string' && <p className="mt-1 text-[12px] leading-relaxed text-gray-600">{diag.resumo}</p>}
              <List title="Evidências" items={arr('evidencias')} />
              <List title="Hipóteses" items={arr('hipoteses')} />
              <List title="Riscos" items={arr('riscos')} tone="text-red-600" />
            </section>

            {plan.length > 0 && (
              <section>
                <p className="mb-1.5 text-[10px] font-bold uppercase tracking-wide text-gray-400">Plano de ação</p>
                <div className="space-y-1.5">
                  {plan.map((a, i) => (
                    <div key={i} className="rounded bg-gray-50 px-2.5 py-1.5">
                      <div className="flex items-center gap-1.5">
                        <span className="rounded bg-gray-200 px-1.5 py-0.5 text-[9px] font-bold uppercase text-gray-600">{a.tipo}</span>
                        <span className="text-[11px] font-semibold text-gray-800">{a.acao}</span>
                      </div>
                      {a.justificativa && <p className="mt-0.5 text-[10px] text-gray-500">{a.justificativa}</p>}
                      {a.impactoEsperado && <p className="text-[10px] text-emerald-600">Impacto esperado: {a.impactoEsperado}</p>}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {detail?.actionHistory && detail.actionHistory.length > 0 && (
              <section>
                <p className="mb-1.5 text-[10px] font-bold uppercase tracking-wide text-gray-400">Histórico de ações</p>
                <div className="space-y-1">
                  {detail.actionHistory.map((h) => (
                    <div key={h.id} className="flex items-center justify-between rounded bg-gray-50 px-2.5 py-1 text-[10px]">
                      <span className="text-gray-700">{ACTION_TYPE_LABELS[h.tipo] || h.tipo}</span>
                      <span className={h.status === 'executada' ? 'text-emerald-600' : h.status === 'falhou' ? 'text-red-600' : 'text-gray-400'}>
                        {h.status}{h.executedAt ? ` · ${new Date(h.executedAt).toLocaleDateString('pt-BR')}` : ''}
                      </span>
                    </div>
                  ))}
                </div>
              </section>
            )}
            <p className="text-[10px] text-gray-400">"Corrigi" inicia o cooldown antes de a IA reanalisar a campanha.</p>
          </>
        )}
      </div>
    </Drawer>
  );
}

export function SellerAdsView({ canTable = true, canBoard = true }: { canTable?: boolean; canBoard?: boolean }) {
  const [params, setParams] = useSearchParams();
  const requestedMode: AdsMode = params.get('view') === 'quadro' ? 'quadro' : 'tabela';
  const mode: AdsMode = !canTable ? 'quadro' : !canBoard ? 'tabela' : requestedMode;
  const setMode = (next: AdsMode) => {
    const p = new URLSearchParams(params);
    if (next === 'tabela') p.delete('view'); else p.set('view', next);
    setParams(p, { replace: true });
  };

  const [marketplace, setMarketplace] = useState<string>('');
  const filters = useMemo(() => (marketplace ? { marketplace } : {}), [marketplace]);
  const agent = useSellerAdsAgent(filters);
  const board = useSellerAdsBoard(filters);
  const running = agent.running || board.running;
  const progress = board.progress?.status === 'running' ? board.progress : agent.progress;

  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [grouping, setGrouping] = useBoardGrouping('seller_ads_board_grouping');
  const [executingId, setExecutingId] = useState<string | null>(null);
  const [showOpportunities, setShowOpportunities] = useState(false);

  const boardCards = useMemo(() => (board.columns ? Object.values(board.columns).flat() : []), [board.columns]);
  const boardByKey = useMemo(() => new Map(boardCards.map((c) => [campaignKey(c), c])), [boardCards]);
  const campaignByKey = useMemo(() => new Map(agent.campaigns.map((c) => [campaignKey(c), c])), [agent.campaigns]);
  const toReview = boardCards.filter((c) => adsFlowStage(c) === 'revisar').length;

  const refreshAll = () => { agent.refetch(); board.refetch(); };

  const handleSync = async () => {
    const res = await agent.run({ type: 'sync' });
    if (res?.success) toast.success('Sincronização iniciada');
    else toast.error(res?.message || 'Falha ao iniciar');
  };

  const handleAnalyze = async () => {
    // Com o quadro liberado roda o agente completo (sincroniza, diagnostica e prioriza).
    const res = canBoard ? await board.run({ type: 'full' }) : await agent.run({ type: 'campaigns' });
    if (res?.success) toast.success('Análise iniciada. As campanhas aparecem no quadro conforme ficam prontas.');
    else toast.error(res?.message || 'Falha ao iniciar');
  };

  const runType = async (type: 'guardrail' | 'portfolio', ok: string) => {
    const res = await board.run({ type });
    if (res?.success) toast.success(ok);
    else toast.error(res?.message || 'Falha ao iniciar');
  };

  const handleExecute = async (action: AdsPendingAction) => {
    if (!window.confirm(`Executar "${ACTION_TYPE_LABELS[action.tipo] || action.tipo}"?\n${describeParams(action)}\n\nIsso altera a campanha na plataforma.`)) return;
    setExecutingId(action.id);
    try {
      const res = await board.executeAction(action.id);
      if (res?.success) { toast.success('Ação executada na plataforma'); agent.refetch(); setSelectedKey(null); }
      else toast.error(res?.message || 'Falha ao executar');
    } finally {
      setExecutingId(null);
    }
  };

  const handleDismiss = async (action: AdsPendingAction) => {
    const res = await board.dismissAction(action.id);
    if (res?.success) { toast.success('Recomendação descartada'); setSelectedKey(null); }
    else toast.error(res?.message || 'Falha ao descartar');
  };

  const handleReview = async (id: string, status: string) => {
    const res = await board.reviewAnalysis(id, { reviewStatus: status });
    if (!res?.success) toast.error(res?.message || 'Falha ao atualizar');
  };

  const handleToggleAnalysis = async (campaignIdExt: string, enabled: boolean) => {
    await agent.setAnalysisEnabled(campaignIdExt, enabled);
    board.refetch();
  };

  const handleAlertUpdate = async (id: string, status: 'resolved' | 'dismissed') => {
    const res = await board.updateAlert(id, status);
    if (!res?.success) toast.error(res?.message || 'Falha ao atualizar alerta');
  };

  const diag = (board.portfolio?.diagnosis || {}) as Record<string, unknown>;
  const insights = Array.isArray(diag.insightsGlobais) ? (diag.insightsGlobais as string[]) : [];

  const progressLabel = progress && progress.status === 'running'
    ? `${progress.type === 'sync' ? 'Sincronizando' : 'Agente rodando'}... ${progress.steps.length > 0 ? progress.steps[progress.steps.length - 1].name : ''}`
    : '';
  const progressPercent = progress && progress.status === 'running' && progress.total > 0 ? (progress.done / Math.max(1, progress.total)) * 100 : null;

  const more: OverflowItem[] = canBoard ? [
    { label: 'Checar gastos agora', hint: 'Regras de segurança: gasto sem venda, ACOS acima do crítico', disabled: running, onClick: () => runType('guardrail', 'Checagem de gastos iniciada') },
    { label: 'Analisar portfólio', hint: 'Visão da IA sobre todas as campanhas juntas', disabled: running, onClick: () => runType('portfolio', 'Análise de portfólio iniciada') },
    { label: 'Atualizar dados da tela', onClick: refreshAll },
  ] : [{ label: 'Atualizar dados da tela', onClick: refreshAll }];

  const selectedBoard = selectedKey ? boardByKey.get(selectedKey) : undefined;
  const selectedCampaign = selectedKey ? campaignByKey.get(selectedKey) : undefined;
  const summary = agent.summary;

  return (
    <div className="mx-auto max-w-7xl space-y-5">
      <SellerAutomationHeader
        title="Ads"
        subtitle="Campanhas patrocinadas da Shopee e do Mercado Livre. Clique numa campanha para ver diagnóstico e ações."
        automationKeys={['ads-sync', ...(canBoard ? ['ads-guardrail', 'ads-portfolio'] : [])]}
        actions={
          <>
            <select value={marketplace} onChange={(e) => setMarketplace(e.target.value)} className={sellerHeaderSelectClass}>
              <option value="">Todos os marketplaces</option>
              <option value="shopee">Shopee</option>
              <option value="mercadolivre">Mercado Livre</option>
            </select>
            <SellerRunButton label="Sincronizar" runningLabel="Sincronizando..." running={running} onClick={handleSync} />
            <CreditConfirmRunButton
              featureKey="ads_campaign_analysis"
              unitLabel="campanha(s)"
              estimate={async () => (await api.estimateSellerAdsAgent({ marketplace: marketplace || undefined })).count}
              onRun={handleAnalyze}
              running={running}
              label="Analisar com IA"
              runningLabel="Analisando..."
              title="Analisar campanhas com IA"
              note="Cobrado por campanha analisada (com IA ligada e que precisa de análise). Campanhas pausadas ou sem dados não são cobradas."
              className={sellerRunButtonClass('primary')}
            />
          </>
        }
        more={more}
        progress={progress && progress.status === 'running' ? { percent: progressPercent, label: progressLabel } : null}
      />

      {progress && progress.status === 'failed' && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2.5 text-xs text-red-700">Última rodada falhou: {progress.error}</div>
      )}

      {summary && (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
          <KpiTile label="Gasto 30d" value={fmtMoney(summary.spend30)} />
          <KpiTile label="Receita Ads 30d" value={fmtMoney(summary.revenue30)} />
          <KpiTile label="ROAS 30d" value={`${summary.roas30}x`} />
          <KpiTile label="ACOS 30d" value={`${summary.acos30}%`} />
          <KpiTile label="TACOS 30d" value={agent.tacos?.tacos != null ? `${agent.tacos.tacos}%` : '—'} />
          <KpiTile label="Campanhas" value={summary.total} />
        </div>
      )}

      {canBoard && board.alerts.length > 0 && (
        <section>
          <h2 className="mb-2 text-sm font-bold text-gray-800">Alertas ({board.alerts.length})</h2>
          <div className="grid gap-2 lg:grid-cols-2">
            {board.alerts.map((a) => <AlertCard key={a.id} alert={a} onUpdate={handleAlertUpdate} />)}
          </div>
        </section>
      )}

      {canBoard && board.portfolio && (diag.headline || insights.length > 0) ? (
        <section className="rounded-xl border border-orange-200 bg-white p-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-bold text-gray-800">Visão do portfólio</h2>
            <span className="text-[10px] text-gray-400">{new Date(board.portfolio.analyzedAt).toLocaleString('pt-BR')}</span>
          </div>
          {typeof diag.headline === 'string' && <p className="mt-1 text-xs font-semibold text-orange-800">{diag.headline}</p>}
          {typeof diag.resumoExecutivo === 'string' && <p className="mt-1 text-[11px] leading-relaxed text-gray-600">{diag.resumoExecutivo}</p>}
          {insights.length > 0 && <ul className="mt-2 space-y-1">{insights.map((i, idx) => <li key={idx} className="text-[11px] text-gray-600">• {i}</li>)}</ul>}
        </section>
      ) : null}

      {canBoard && board.opportunities.length > 0 && (
        <section>
          <button onClick={() => setShowOpportunities((v) => !v)} className="flex items-center gap-1.5 text-sm font-bold text-gray-800">
            Produtos que vendem bem sem Ads ({board.opportunities.length})
            <span className="text-[10px] font-normal text-gray-400">{showOpportunities ? 'ocultar' : 'mostrar'}</span>
          </button>
          {showOpportunities && (
            <div className="mt-2 grid gap-2 sm:grid-cols-2">
              {board.opportunities.map((op) => <OpportunityRow key={`${op.connectionId}-${op.listingIdExt}`} op={op} />)}
            </div>
          )}
        </section>
      )}

      <div className="flex items-center justify-between gap-3">
        {canTable && canBoard ? (
          <SegmentedToggle<AdsMode>
            value={mode}
            onChange={setMode}
            options={[{ value: 'tabela', label: 'Tabela' }, { value: 'quadro', label: 'Quadro', count: toReview || undefined }]}
          />
        ) : <span />}
        <div className="flex items-center gap-3">
          {mode === 'quadro' && <BoardGroupingToggle value={grouping} onChange={setGrouping} />}
          {(agent.loading || board.loading) && <span className="text-[11px] text-gray-400">Carregando...</span>}
        </div>
      </div>

      {mode === 'quadro' && (
        boardCards.length === 0 ? (
          <EmptyState title="Nenhuma campanha analisada ainda">Clique em "Analisar com IA" para sincronizar, diagnosticar e priorizar as campanhas.</EmptyState>
        ) : grouping === 'saude' ? (
          <HealthBoard items={boardCards} columns={ADS_HEALTH_COLUMNS} renderCard={(card) => <AdsBoardCardView card={card} onClick={() => setSelectedKey(campaignKey(card))} />} />
        ) : (
          <FlowBoard
            items={boardCards}
            stageOf={adsFlowStage}
            renderCard={(card) => <AdsBoardCardView card={card} onClick={() => setSelectedKey(campaignKey(card))} />}
            onMove={(card, _stage, reviewStatus) => handleReview(card.id, reviewStatus)}
            emptyHint="Nenhuma campanha pendente"
          />
        )
      )}

      {mode === 'tabela' && (
        agent.campaigns.length === 0 && !agent.loading ? (
          <EmptyState title="Nenhuma campanha sincronizada ainda">Clique em "Sincronizar" para importar as campanhas da Shopee e do Mercado Livre.</EmptyState>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
            <table className="w-full min-w-[900px] text-sm">
              <thead className="border-b border-gray-100 bg-gray-50/80 text-left text-xs font-medium text-gray-500">
                <tr>
                  <th className="px-3 py-2.5">Campanha</th>
                  <th className="px-3 py-2.5">Orçamento</th>
                  <th className="px-3 py-2.5">Gasto 30d</th>
                  <th className="px-3 py-2.5">Receita 30d</th>
                  <th className="px-3 py-2.5">ROAS</th>
                  <th className="px-3 py-2.5">ACOS</th>
                  {canBoard && <th className="px-3 py-2.5">Saúde</th>}
                  <th className="px-3 py-2.5 text-center" title="Incluir esta campanha quando rodar a análise com IA">IA</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {agent.campaigns.map((c) => {
                  const key = campaignKey(c);
                  const bc = boardByKey.get(key);
                  const style = CLASSIFICATION_STYLES[c.classification] || CLASSIFICATION_STYLES.no_data;
                  const stage = bc ? adsFlowStage(bc) : null;
                  return (
                    <tr key={key} className="cursor-pointer transition hover:bg-orange-50/40" onClick={() => setSelectedKey(key)}>
                      <td className="px-3 py-2.5">
                        <div className="flex items-start gap-2">
                          <span className="mt-0.5"><PlatformIcon platform={c.marketplace} size={14} /></span>
                          <div className="min-w-0">
                            <p className="max-w-[320px] truncate font-medium text-gray-900" title={c.name}>{c.name}</p>
                            <p className="text-[10px] text-gray-500">{[c.status, c.lifecycleStage && `${c.lifecycleStage} · ${c.ageDays}d`, c.itemCount ? `${c.itemCount} item(ns)` : null].filter(Boolean).join(' · ')}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-xs text-gray-700">{c.budget != null ? `${fmtMoney(c.budget)}${c.budgetType === 'automatic' ? ' (auto)' : '/dia'}` : '—'}</td>
                      <td className="px-3 py-2.5 text-xs text-gray-700">{fmtMoney(c.d30.spend)}</td>
                      <td className="px-3 py-2.5 text-xs text-gray-700">{fmtMoney(c.d30.totalRevenue)}</td>
                      <td className="px-3 py-2.5 text-xs font-medium text-gray-900">{c.d30.roas}x</td>
                      <td className="px-3 py-2.5 text-xs text-gray-700">{c.d30.acos >= 999 ? '—' : `${c.d30.acos}%`}</td>
                      {canBoard && (
                        <td className="px-3 py-2.5">
                          <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${style.badge}`}>{style.label}</span>
                          {stage && stage !== 'revisar' && stage !== 'ok' && <span className="ml-1 text-[10px] text-gray-500">{stage === 'revisando' ? 'revisando' : 'aguardando'}</span>}
                          {(bc?.pendingActionsCount || 0) > 0 && <span className="ml-1 rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700">{bc!.pendingActionsCount} ação</span>}
                        </td>
                      )}
                      <td className="px-3 py-2.5 text-center" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          onClick={() => handleToggleAnalysis(c.campaignIdExt, !c.analysisEnabled)}
                          className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-[10px] font-semibold transition ${c.analysisEnabled ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                        >
                          <span className={`h-1.5 w-1.5 rounded-full ${c.analysisEnabled ? 'bg-emerald-500' : 'bg-gray-400'}`} />
                          {c.analysisEnabled ? 'ON' : 'OFF'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      )}

      {selectedKey && (selectedBoard || selectedCampaign) && (
        <AdsCampaignDrawer
          name={selectedBoard?.campaignName || selectedCampaign?.name || ''}
          marketplace={selectedBoard?.marketplace || selectedCampaign?.marketplace || ''}
          campaign={selectedCampaign}
          boardCard={canBoard ? selectedBoard : undefined}
          onClose={() => setSelectedKey(null)}
          fetchDetail={board.fetchAnalysisDetail}
          onExecute={handleExecute}
          onDismiss={handleDismiss}
          onReview={handleReview}
          onToggleAnalysis={handleToggleAnalysis}
          executingId={executingId}
        />
      )}
    </div>
  );
}
